import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  AlertTitle,
  useTheme
} from '@mui/material';

// Import the SankeyDiagram component
import SankeyDiagram from '../../components/dashboard/SankeyDiagram';

// Future component imports
// import SupplierCountChart from '../../components/dashboard/SupplierCountChart';
// import CategoryFragmentationChart from '../../components/dashboard/CategoryFragmentationChart';
// import ConsolidationOpportunityTable from '../../components/dashboard/ConsolidationOpportunityTable';

interface Supplier {
  name: string;
  spend: number;
  percentage: number;
}

interface Category {
  name: string;
  suppliers: Supplier[];
  totalSpend: number;
  fragmentation: number;
  consolidationPotential: boolean;
  singleSupplierRisk: boolean;
}

const SupplierConsolidationPage: React.FC = () => {
  const theme = useTheme();

  // Mock data for demonstration
  const [categories, setCategories] = useState<Category[]>([
    {
      name: 'IT Services',
      suppliers: [
        { name: 'TechCorp', spend: 3500000, percentage: 28 },
        { name: 'IT Solutions Inc', spend: 2800000, percentage: 22 },
        { name: 'Digital Consulting', spend: 2200000, percentage: 18 },
        { name: 'InfraSupport Co', spend: 1900000, percentage: 15 },
        { name: 'CloudTech Solutions', spend: 1400000, percentage: 11 },
        { name: 'Various IT Vendors', spend: 700000, percentage: 6 }
      ],
      totalSpend: 12500000,
      fragmentation: 6,
      consolidationPotential: true,
      singleSupplierRisk: false
    },
    {
      name: 'Office Supplies',
      suppliers: [
        { name: 'Office Depot', spend: 3200000, percentage: 75 },
        { name: 'Staples', spend: 800000, percentage: 18 },
        { name: 'Various Local Suppliers', spend: 300000, percentage: 7 }
      ],
      totalSpend: 4300000,
      fragmentation: 3,
      consolidationPotential: true,
      singleSupplierRisk: false
    },
    {
      name: 'Marketing Services',
      suppliers: [
        { name: 'Global Marketing Inc', spend: 3900000, percentage: 93 },
        { name: 'Various Marketing Vendors', spend: 300000, percentage: 7 }
      ],
      totalSpend: 4200000,
      fragmentation: 2,
      consolidationPotential: false,
      singleSupplierRisk: true
    },
    {
      name: 'Logistics',
      suppliers: [
        { name: 'ShipFast', spend: 1600000, percentage: 30 },
        { name: 'Global Logistics', spend: 1400000, percentage: 26 },
        { name: 'QuickShip', spend: 1200000, percentage: 22 },
        { name: 'Regional Transporters', spend: 700000, percentage: 13 },
        { name: 'Express Delivery', spend: 500000, percentage: 9 }
      ],
      totalSpend: 5400000,
      fragmentation: 5,
      consolidationPotential: true,
      singleSupplierRisk: false
    }
  ]);

  // Business Rules Implementation
  const fragmentationThreshold = 4; // If a category has more than 4 suppliers
  const topSupplierThreshold = 20; // Top supplier should have at least 20% share to not be fragmented
  const singleSupplierThreshold = 90; // If a supplier has more than 90% share, flag sole-sourcing risk

  // Find the categories that meet the business rule conditions
  const fragmentedCategories = categories.filter(
    cat => cat.fragmentation > fragmentationThreshold || 
    (cat.suppliers.length > 0 && cat.suppliers[0].percentage < topSupplierThreshold)
  );

  const consolidationPotentialCategories = categories.filter(
    cat => cat.suppliers.length >= 3 && 
    cat.suppliers.slice(0, 3).reduce((sum, supplier) => sum + supplier.percentage, 0) > 50
  );

  const singleSupplierRiskCategories = categories.filter(
    cat => cat.suppliers.length > 0 && cat.suppliers[0].percentage > singleSupplierThreshold
  );

  // Alerts based on business rules
  const fragmentationAlert = fragmentedCategories.length > 0 && (
    <Alert severity="info" sx={{ mt: 2 }}>
      <AlertTitle>Supplier Fragmentation Detected</AlertTitle>
      {fragmentedCategories.map((cat, index) => (
        <div key={index}>
          {cat.name}: {cat.fragmentation} suppliers, top supplier has {cat.suppliers[0]?.percentage || 0}% share.
        </div>
      ))}
    </Alert>
  );

  const consolidationAlert = consolidationPotentialCategories.length > 0 && (
    <Alert severity="success" sx={{ mt: 2 }}>
      <AlertTitle>Consolidation Opportunities</AlertTitle>
      {consolidationPotentialCategories.map((cat, index) => (
        <div key={index}>
          {cat.name}: Top 3 suppliers represent 
          {cat.suppliers.slice(0, 3).reduce((sum, supplier) => sum + supplier.percentage, 0)}% 
          of spend. Consider consolidation.
        </div>
      ))}
    </Alert>
  );

  const singleSupplierAlert = singleSupplierRiskCategories.length > 0 && (
    <Alert severity="warning" sx={{ mt: 2 }}>
      <AlertTitle>Single-Supplier Risk</AlertTitle>
      {singleSupplierRiskCategories.map((cat, index) => (
        <div key={index}>
          {cat.name}: {cat.suppliers[0]?.name} represents {cat.suppliers[0]?.percentage}% of spend. 
          Consider diversifying suppliers.
        </div>
      ))}
    </Alert>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Supplier Consolidation Opportunity
      </Typography>

      <Grid container spacing={3}>
        {/* Supplier Count Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Supplier Count by Category
              {/* <SupplierCountChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Category Fragmentation Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Category Fragmentation Bubble Chart
              {/* <CategoryFragmentationChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Sankey Diagram */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, minHeight: '600px', height: 'auto' }}>
            <SankeyDiagram categories={categories} />
          </Paper>
        </Grid>

        {/* Business Rules Alerts */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Supplier Consolidation Insights
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1" sx={{ mb: 2 }}>
                Analysis of {categories.length} spending categories across {
                  categories.reduce((sum, cat) => sum + cat.suppliers.length, 0)
                } suppliers.
              </Typography>
              {fragmentationAlert}
              {consolidationAlert}
              {singleSupplierAlert}
            </CardContent>
          </Card>
        </Grid>

        {/* Consolidation Opportunities Table - placeholder */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Consolidation Opportunities Table
              {/* <ConsolidationOpportunityTable /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SupplierConsolidationPage; 